/**
 * Schedule View — the visit checklist (Phase 10).
 *
 * ⛔ KEEP THIS FILE IMPORT-FREE (the node-tests execute it verbatim). The one thing it needs from
 * alerts.js — the sentence for each alert — is INJECTED as `describe`, the same way summaryCounts()
 * takes `bucketOf`. That keeps exactly one copy of every sentence: the line under a checklist item
 * is word for word what the block's icon already says.
 *
 * ── WHAT IT IS ─────────────────────────────────────────────────────────────────────────────────
 * The block's alerts, regrouped by what the front desk does about them (Medical, Insurance, Forms,
 * Tasks, Follow-ups, AR, Treatment) and split into to-dos (a checkbox) and FYIs (a "!"). The design
 * is `hovercard.jsx` in the 2026-10-02 Claude Design export.
 *
 * ── ⛔ BUILT FROM THE BLOCK'S OWN `alertKeys` ───────────────────────────────────────────────────
 * The caller passes the exact list the block draws (visibleAlertKeys + the office's switches). A
 * checklist built from the raw payload would list an alert the office switched off, or one whose
 * rule failed, while the block shows no icon for it — and staff could not tell which to believe.
 *
 * ── ⛔ A TICK IS A MARK ON THIS VISIT, NOTHING MORE ─────────────────────────────────────────────
 * Ticking "Collect balance" does not post a payment, ticking a task does not complete it. Ticks
 * are stored per (AptNum, item id) in teamio.schedule_view_checks, so an item id must be STABLE
 * across refreshes (or every tick silently disappears) and fit the ItemKey column.
 */

/** Width of teamio.schedule_view_checks.ItemKey. */
export const ITEM_KEY_MAX = 64;

const ITEM_KEY_RE = /^[A-Za-z0-9:_.-]+$/;

/**
 * The groups, in the design's order. `tab` is the Patient Panel tab the group's arrow opens — every
 * one must be in patientPanel.js PANEL_TABS and must be a tab the panel actually draws ENABLED (an
 * arrow into a disabled tab would land on Overview and look broken). Follow-ups was `null` until the
 * panel's Follow-ups tab was built; it is live now, so its arrow opens it.
 */
export const CHECKLIST_CATEGORIES = [
  { key: 'medical', label: 'Medical', icon: 'fa-medkit', tab: 'overview' },
  { key: 'insurance', label: 'Insurance', icon: 'fa-shield', tab: 'overview' },
  { key: 'forms', label: 'Forms', icon: 'fa-wpforms', tab: 'forms' },
  { key: 'tasks', label: 'Tasks', icon: 'fa-tasks', tab: 'tasks' },
  { key: 'followups', label: 'Open follow-ups', icon: 'fa-bell-o', tab: 'followups' },
  { key: 'ar', label: 'AR', icon: 'fa-dollar', tab: 'financials' },
  { key: 'treatment', label: 'Treatment', icon: 'fa-wrench', tab: 'treatment' },
];

/**
 * Which group each alert lands in. Every key in settingsForm.js ALERT_KEYS must be here — pinned
 * by cypress/node-tests/schedule-view-checklist.test.js ("AGREEMENT").
 */
export const ALERT_CATEGORY = {
  premed: 'medical',
  insverify: 'insurance',
  forms: 'forms',
  medhx: 'forms',
  task: 'tasks',
  followup: 'followups',
  balance: 'ar',
  claim: 'ar',
  txsign: 'treatment',
  unsched: 'treatment',
  recall: 'treatment',
};

/** The key a tick is stored under in the store: "<AptNum>:<ItemKey>". */
export function checkKey(aptNum, itemKey) {
  return `${aptNum}:${itemKey}`;
}

/** Fits the column and carries nothing a SQL string or a Firestore path could trip on. */
export function isValidItemKey(key) {
  return typeof key === 'string' && key.length > 0 && key.length <= ITEM_KEY_MAX && ITEM_KEY_RE.test(key);
}

/** A short, stable hash (djb2) — for ids built from names, which can be any length. */
function hash(text) {
  let h = 5381;
  const s = String(text || '');
  for (let i = 0; i < s.length; i += 1) h = ((h << 5) + h + s.charCodeAt(i)) >>> 0;
  return h.toString(36);
}

/**
 * `<prefix>:<raw>` when raw is already a safe, short id; otherwise `<prefix>:h<hash>` — so a
 * Firestore id or a form UUID is kept as-is (readable in the table), and anything odd is made safe
 * without ever being passed through.
 */
function makeId(prefix, raw) {
  const direct = `${prefix}:${raw}`;
  if (raw !== undefined && raw !== null && raw !== '' && isValidItemKey(direct)) return direct;
  return `${prefix}:h${hash(raw)}`;
}

function money(amount) {
  if (amount === null || amount === undefined || amount === '') return null;
  const n = Number(amount);
  if (!Number.isFinite(n)) return null;
  return `$${Math.round(n).toLocaleString('en-US')}`;
}

/**
 * "MM/DD/YYYY ..." (how the tasks store formats dueAt) → { ymd: 'YYYY-MM-DD', short: 'M/D' }, or
 * null. Parsed by hand — no Date, no moment — so it is the same in every browser and timezone.
 */
function parseDue(dueAt) {
  const m = /^(\d{1,2})\/(\d{1,2})\/(\d{4})/.exec(String(dueAt || ''));
  if (!m) return null;
  const mm = Number(m[1]);
  const dd = Number(m[2]);
  if (mm < 1 || mm > 12 || dd < 1 || dd > 31) return null;
  const pad = n => (n < 10 ? `0${n}` : String(n));
  return { ymd: `${m[3]}-${pad(mm)}-${pad(dd)}`, short: `${mm}/${dd}` };
}

/** One line per form or packet; never-sent and sent-waiting stay different lines. */
function formItems(detail, describe) {
  const d = detail || {};
  const lines = [];

  if (Array.isArray(d.items) && d.items.length) {
    d.items.forEach((it) => {
      if (!it || !it.name) return;
      // A form carries its FormIDForWeb; a packet carries null (schedule-view-service does not
      // select PacketID), so it is keyed by kind + name — stable unless the packet is renamed.
      const raw = it.id || `${it.kind || 'item'}-${it.name}`;
      lines.push({ id: makeId('forms', raw), name: it.name, state: it.state === 'notSent' ? 'notSent' : 'waiting' });
    });
  } else if (Array.isArray(d.waiting) || Array.isArray(d.notSent)) {
    (d.notSent || []).forEach(name => lines.push({ id: makeId('forms', `n${hash(name)}`), name, state: 'notSent' }));
    (d.waiting || []).forEach(name => lines.push({ id: makeId('forms', `w${hash(name)}`), name, state: 'waiting' }));
  }

  if (!lines.length) {
    // A payload from a service that predates the per-item lists: one line, the plain sentence.
    return [{
      id: 'forms', kind: 'action', phase: 'prep', title: 'Forms outstanding', short: 'Forms outstanding',
      sub: describe('forms', d), meta: null,
    }];
  }

  return lines.map(line => (line.state === 'notSent'
    ? {
      id: line.id, kind: 'action', phase: 'prep',
      title: `${line.name} not sent yet`, short: `Send ${line.name}`,
      sub: 'Not sent to the patient yet — the office needs to send it.', meta: null,
    }
    : {
      id: line.id, kind: 'action', phase: 'prep',
      title: `${line.name} not submitted`, short: `${line.name} — not submitted`,
      sub: 'Sent — waiting on the patient to complete it.', meta: null,
    }));
}

/** One line per open task when the titles are known; otherwise one line carrying the count. */
function taskItems(detail, tasks, describe, today) {
  if (!Array.isArray(tasks) || !tasks.length) {
    return [{
      id: 'task', kind: 'action', phase: 'prep', title: 'Open tasks', short: 'Open tasks',
      sub: describe('task', detail), meta: null,
    }];
  }
  return tasks.map((t) => {
    const due = parseDue(t && t.dueAt);
    const late = !!(due && today && due.ymd < today);
    const text = String((t && t.text) || '').trim() || 'Untitled task';
    return {
      id: makeId('task', t && t.id),
      kind: 'action',
      phase: 'prep',
      title: text,
      short: text,
      sub: t && t.who ? `Assigned to ${t.who}` : 'Unassigned',
      meta: due ? { text: `Due ${due.short}`, tone: late ? 'late' : '' } : null,
    };
  });
}

/** The single line for every other alert. `sub` is always describeAlert's sentence. */
function alertItem(key, d, describe) {
  const sub = describe(key, d);
  switch (key) {
    case 'premed':
      // ⛔ An FYI, never a checkbox: OD records that premedication is REQUIRED, and nothing anywhere
      // records that it was taken — a tick here would read as exactly that.
      return { id: key, kind: 'info', tone: 'crit', title: 'Premedication required', short: 'Premed required', sub, meta: null };
    case 'insverify':
      return {
        id: key, kind: 'action', phase: 'prep',
        title: d.neverVerified ? 'Insurance never verified' : 'Insurance verification out of date',
        short: 'Verify eligibility', sub, meta: null,
      };
    case 'medhx':
      return { id: key, kind: 'action', phase: 'arrive', title: 'Medical history update due', short: 'Update medical history', sub, meta: null };
    case 'followup':
      return {
        id: key, kind: 'action', phase: 'prep',
        title: Number(d.count) === 1 ? 'Open follow-up' : 'Open follow-ups', short: 'Work open follow-ups',
        sub, meta: d.pastDue ? { text: 'Past due', tone: 'late' } : null,
      };
    case 'balance': {
      const amt = money(d.amount);
      return { id: key, kind: 'action', phase: 'arrive', title: 'Outstanding balance', short: 'Collect balance', sub, meta: amt ? { text: amt, tone: 'amt' } : null };
    }
    case 'claim': {
      const days = Number(d.daysOutstanding);
      return {
        id: key, kind: 'action', phase: 'prep', title: 'Unpaid claims over 30 days', short: 'Chase unpaid claims',
        sub, meta: Number.isFinite(days) && days > 0 ? { text: `${days} days`, tone: 'late' } : null,
      };
    }
    case 'txsign':
      return { id: key, kind: 'action', phase: 'leave', title: 'Treatment plan not signed', short: 'Get treatment plan signed', sub, meta: null };
    case 'unsched': {
      const amt = money(d.amount);
      return { id: key, kind: 'action', phase: 'leave', title: 'Unscheduled treatment', short: 'Schedule remaining treatment', sub, meta: amt ? { text: amt, tone: 'amt' } : null };
    }
    case 'recall':
      return { id: key, kind: 'action', phase: 'leave', title: 'Overdue for recall', short: 'Book recall', sub, meta: null };
    default:
      return null;
  }
}

/**
 * The checklist for one appointment.
 *
 * @param {Object}   o
 * @param {string[]} o.alertKeys  exactly what the block draws, in its order
 * @param {Object}   o.details    alertsForPatient(payload, PatNum) — key → detail object
 * @param {Array}    [o.tasks]    open tasks for this patient: { id, text, dueAt, who }
 * @param {Function} o.describe   alerts.js describeAlert (injected — see the header)
 * @param {string}   [o.today]    'YYYY-MM-DD' in the office's timezone, for "past due"
 * @returns {Array} [{ key, label, icon, tab, items: [{ id, kind, tone?, phase?, title, short, sub, meta }] }]
 */
export function buildChecklist({ alertKeys, details, tasks, describe, today } = {}) {
  const keys = Array.isArray(alertKeys) ? alertKeys : [];
  const det = details || {};
  const say = typeof describe === 'function' ? describe : () => '';
  const byCat = {};
  const add = (cat, items) => { byCat[cat] = (byCat[cat] || []).concat(items); };

  keys.forEach((key) => {
    const cat = ALERT_CATEGORY[key];
    const d = det[key];
    if (!cat || !d || typeof d !== 'object') return;
    if (key === 'forms') add(cat, formItems(d, say));
    else if (key === 'task') add(cat, taskItems(d, tasks, say, today));
    else {
      const item = alertItem(key, d, say);
      if (item) add(cat, [item]);
    }
  });

  return CHECKLIST_CATEGORIES
    .filter(c => byCat[c.key] && byCat[c.key].length)
    .map(c => Object.assign({}, c, { items: byCat[c.key] }));
}

/**
 * { todo, done, fyi } for one appointment. Only ticks on items that EXIST right now count — a tick
 * left behind by an item that has since cleared (the form came back) must not push the bar past
 * 100%, and an FYI can never be done.
 */
export function checklistTotals(categories, checks, aptNum) {
  const marks = checks || {};
  let todo = 0;
  let done = 0;
  let fyi = 0;
  (categories || []).forEach((c) => {
    (c.items || []).forEach((it) => {
      if (it.kind !== 'action') { fyi += 1; return; }
      todo += 1;
      if (marks[checkKey(aptNum, it.id)]) done += 1;
    });
  });
  return { todo, done, fyi };
}

/**
 * The alert an item came from. Item ids are the alert key, or `<alert key>:<suffix>` for the alerts
 * that expand to one line per form / task — so the part before the first colon IS the key. Lets a
 * test (and the Table view) prove every line traces back to an alert the block is drawing.
 */
export function itemAlertKey(itemId) {
  return String(itemId || '').split(':')[0];
}

/**
 * The checklist for EVERY appointment on the day on screen, built once per render.
 *
 * ⛔ This is the one place that decides what the block's chip, the card and (Phase 11) a table row
 * say. It takes its alert keys from `deps.blockAlertKeys` — the SAME function the block's icon row
 * and the phone card call — so the icons, the chip and the card cannot disagree. alerts.js is
 * import-free like this file, so its functions arrive as `deps` (the page passes the real ones; the
 * node-tests pass the real ones loaded from source):
 *   deps.blockAlertKeys(payload, patNum, viewSettings, order) → string[]
 *   deps.alertsForPatient(payload, patNum)                    → { key: detail }
 *   deps.describe(key, detail)                                → the sentence
 *
 * @param {Object}   o
 * @param {Array}    o.appointments   the FILTERED day's appointments
 * @param {Object}   o.payload        the /alerts payload for the day (null before it arrives)
 * @param {Object}   o.viewSettings   the office's resolved settings
 * @param {string[]} o.order          ALERT_KEYS
 * @param {Object}   [o.tasksByPatNum]  taskLinesByPatNum
 * @param {Object}   [o.checks]       the store's ticks, '<AptNum>:<ItemKey>' → { by, at }
 * @param {string}   [o.today]        'YYYY-MM-DD' at the office
 * @returns {Object} { '<AptNum>': { alertKeys, categories, totals } }
 */
export function checklistsForDay({
  appointments, payload, viewSettings, order, tasksByPatNum, checks, today, deps,
} = {}) {
  const out = {};
  const d = deps || {};
  if (typeof d.blockAlertKeys !== 'function' || typeof d.alertsForPatient !== 'function') return out;
  const tasks = tasksByPatNum || {};
  (appointments || []).forEach((appt) => {
    if (!appt || appt.AptNum === null || appt.AptNum === undefined) return;
    const alertKeys = d.blockAlertKeys(payload, appt.PatNum, viewSettings, order);
    const categories = buildChecklist({
      alertKeys,
      details: d.alertsForPatient(payload, appt.PatNum),
      tasks: tasks[String(appt.PatNum)],
      describe: d.describe,
      today,
    });
    out[appt.AptNum] = { alertKeys, categories, totals: checklistTotals(categories, checks, appt.AptNum) };
  });
  return out;
}
